import React, { useEffect, useRef, useState, useMemo } from "react";
import useBackdropClose from "../../hooks/useBackdropClose";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { API_URL } from "../../config/constants";
import { avatarList } from "../../assets/avatars";
import {
  Overlay, Modal, ModalTitle, SectionTitle, Divider, Grid,
  Field, Label, Input, Select, Textarea, Hint,
  CheckRow, Checkbox, CheckLabel,
  AvatarGrid, AvatarOption,
  ErrorMsg, ActionRow, CancelButton, SubmitButton, AutoField,
  ConfirmOverlay, ConfirmModal, ConfirmTitle, ConfirmText,
  ConfirmActions, ConfirmCancelButton, DiscardButton,
} from "./styles";

const COHORTS = [1, 2];

const calcCorrectedAge = (dob, treatmentStart, isPremature, expectedDelivery) => {
  const baseDate = isPremature ? expectedDelivery : dob;
  if (!baseDate || !treatmentStart) return "";
  const base = new Date(baseDate);
  const end  = new Date(treatmentStart);
  if (end < base) return "";

  let years  = end.getFullYear() - base.getFullYear();
  let months = end.getMonth()    - base.getMonth();
  let days   = end.getDate()     - base.getDate();

  if (days < 0) {
    months--;
    days += new Date(end.getFullYear(), end.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  const parts = [];
  if (years  > 0) parts.push(`${years} year${years   !== 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} month${months !== 1 ? "s" : ""}`);
  if (days   > 0) parts.push(`${days} day${days       !== 1 ? "s" : ""}`);

  return parts.length > 0 ? parts.join(", ") : "0 days";
};

const toInputDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
};

const INITIAL = {
  patient_code: "",
  sex: "",
  date_of_birth: "",
  is_premature: false,
  expected_delivery_date: "",
  treatment_start_date: "",
  assigned_therapist_id: "",
  pathology_id: "",
  secondary_diagnosis: "",
  cohort: "",
  notes_observations: "",
  username: "",
  password: "",
  avatar: "",
};

const fromPatient = (p) => ({
  patient_code: p.patient_code || "",
  sex: p.sex || "",
  date_of_birth: toInputDate(p.date_of_birth),
  is_premature: !!p.is_premature,
  expected_delivery_date: toInputDate(p.expected_delivery_date),
  treatment_start_date: toInputDate(p.treatment_start_date),
  assigned_therapist_id: p.assigned_therapist_id ? String(p.assigned_therapist_id) : "",
  pathology_id: p.pathology_id ? String(p.pathology_id) : "",
  secondary_diagnosis: p.secondary_diagnosis || "",
  cohort: p.cohort != null ? String(p.cohort) : "",
  notes_observations: p.notes_observations || "",
  username: p.username || "",
  password: "",
  avatar: p.avatar_url || "",
});

const AddPatientModal = ({ onClose, onCreated, patient, onUpdated }) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const isEdit = !!patient;

  const [form, setForm] = useState(isEdit ? fromPatient(patient) : INITIAL);
  const initialForm = useRef(isEdit ? fromPatient(patient) : INITIAL);
  const [showDiscard, setShowDiscard] = useState(false);
  const [therapists, setTherapists] = useState([]);
  const [pathologies, setPathologies] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [loading, setLoading] = useState(false);
  const discardBackdrop = useBackdropClose(() => setShowDiscard(false));

  useEffect(() => {
    fetch(`${API_URL}/hospitals/therapists`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setTherapists(Array.isArray(d) ? d : []))
      .catch(() => {});

    fetch(`${API_URL}/pathologies`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setPathologies(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const correctedAge = useMemo(
    () =>
      calcCorrectedAge(
        form.date_of_birth,
        form.treatment_start_date,
        form.is_premature,
        form.expected_delivery_date
      ),
    [form.date_of_birth, form.treatment_start_date, form.is_premature, form.expected_delivery_date]
  );

  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    setGlobalError("");
  };

  // Solo preguntamos si de verdad hay algo que perder.
  const handleCancel = () => {
    if (JSON.stringify(form) === JSON.stringify(initialForm.current)) onClose();
    else setShowDiscard(true);
  };

  const validate = () => {
    const errors = {};
    if (!isEdit) {
      if (!form.patient_code) {
        errors.patient_code = t("addPatient.errors.patientCodeRequired");
      } else if (!/^[a-zA-Z0-9]{8,}$/.test(form.patient_code)) {
        errors.patient_code = t("addPatient.errors.patientCodeFormat");
      }
    }
    if (!form.username) errors.username = t("addPatient.errors.usernameRequired");
    if (!isEdit) {
      if (!form.password) {
        errors.password = t("addPatient.errors.passwordRequired");
      } else if (form.password.length < 8) {
        errors.password = t("addPatient.errors.passwordLength");
      }
    } else if (form.password && form.password.length < 8) {
      errors.password = t("addPatient.errors.passwordLength");
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }

    setLoading(true);
    setFieldErrors({});
    setGlobalError("");
    try {
      const base = {
        patient_code: form.patient_code,
        sex: form.sex,
        date_of_birth: form.date_of_birth || null,
        is_premature: form.is_premature ? 1 : 0,
        expected_delivery_date: form.is_premature ? form.expected_delivery_date || null : null,
        treatment_start_date: form.treatment_start_date || null,
        age_at_start_corrected: correctedAge || null,
        assigned_therapist_id: form.assigned_therapist_id ? Number(form.assigned_therapist_id) : null,
        pathology_id: form.pathology_id ? Number(form.pathology_id) : null,
        secondary_diagnosis: form.secondary_diagnosis || null,
        cohort: form.cohort ? Number(form.cohort) : null,
        notes_observations: form.notes_observations,
        avatar_url: form.avatar || null,
      };

      let res;
      if (isEdit) {
        res = await fetch(`${API_URL}/patients/${patient.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            ...base,
            username: form.username,
            ...(form.password ? { password: form.password } : {}),
          }),
        });
      } else {
        res = await fetch(`${API_URL}/patients`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...base, username: form.username, password: form.password }),
        });
      }

      const data = await res.json();
      if (!res.ok) {
        if (data.error?.includes("Patient ID")) setFieldErrors({ patient_code: data.error });
        else if (data.error?.includes("Username")) setFieldErrors({ username: data.error });
        else setGlobalError(data.error || t("addPatient.errors.generic"));
        return;
      }
      if (isEdit) {
        toast.success(t("addPatient.toastUpdated", { code: form.patient_code }));
        onUpdated();
      } else {
        onCreated();
      }
    } catch {
      setGlobalError(t("addPatient.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const fe = fieldErrors;

  return (
    <Overlay>
      <Modal>
        <ModalTitle>{isEdit ? t("addPatient.editTitle") : t("addPatient.title")}</ModalTitle>

        <form onSubmit={handleSubmit} style={{ display: "contents" }}>
          <Grid>
            <Field>
              <Label>{t("addPatient.userId")}</Label>
              {isEdit ? (
                <AutoField>{form.patient_code || "—"}</AutoField>
              ) : (
                <>
                  <Input
                    $error={!!fe.patient_code}
                    placeholder={t("addPatient.userIdPlaceholder")}
                    value={form.patient_code}
                    onChange={set("patient_code")}
                  />
                  {fe.patient_code ? <ErrorMsg>{fe.patient_code}</ErrorMsg> : <Hint>{t("addPatient.userIdHint")}</Hint>}
                </>
              )}
            </Field>

            <Field>
              <Label>{t("addPatient.treatmentStartDate")}</Label>
              <Input
                type="date"
                value={form.treatment_start_date}
                onChange={set("treatment_start_date")}
              />
            </Field>

            <Field>
              <Label>{t("addPatient.ageAtStart")}</Label>
              <AutoField>{correctedAge || "—"}</AutoField>
              <Hint>{t("addPatient.ageAtStartHint")}</Hint>
            </Field>

            <Field>
              <Label>{t("addPatient.sex")}</Label>
              <Select value={form.sex} onChange={set("sex")}>
                <option value="">{t("addPatient.sexPlaceholder")}</option>
                <option value="Female">{t("addPatient.female")}</option>
                <option value="Male">{t("addPatient.male")}</option>
              </Select>
            </Field>

            <Field>
              <Label>{t("addPatient.dateOfBirth")}</Label>
              <Input
                type="date"
                value={form.date_of_birth}
                onChange={set("date_of_birth")}
              />
            </Field>

            <Field>
              <Label>{t("addPatient.assignedTherapist")}</Label>
              <Select value={form.assigned_therapist_id} onChange={set("assigned_therapist_id")}>
                <option value="">{t("addPatient.assignedTherapistPlaceholder")}</option>
                {therapists.map((th) => (
                  <option key={th.id} value={th.id}>
                    {th.display_name || th.username}
                  </option>
                ))}
              </Select>
            </Field>
          </Grid>

          <CheckRow>
            <Checkbox
              type="checkbox"
              id="is_premature"
              checked={form.is_premature}
              onChange={set("is_premature")}
            />
            <CheckLabel htmlFor="is_premature">{t("addPatient.isPremature")}</CheckLabel>
          </CheckRow>

          {form.is_premature && (
            <Field>
              <Label>{t("addPatient.expectedDelivery")}</Label>
              <Input
                type="date"
                value={form.expected_delivery_date}
                onChange={set("expected_delivery_date")}
              />
            </Field>
          )}

          <Grid>
            <Field>
              <Label>{t("addPatient.cohort")}</Label>
              <Select value={form.cohort} onChange={set("cohort")}>
                <option value="">{t("addPatient.cohortPlaceholder")}</option>
                {COHORTS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Select>
            </Field>

            <Field>
              <Label>{t("addPatient.pathology")}</Label>
              <Select value={form.pathology_id} onChange={set("pathology_id")}>
                <option value="">{t("addPatient.pathologyPlaceholder")}</option>
                {pathologies.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </Field>
          </Grid>

          <Field>
            <Label>{t("addPatient.secondaryDiagnosis")}</Label>
            <Input
              type="text"
              value={form.secondary_diagnosis}
              onChange={set("secondary_diagnosis")}
              placeholder={t("addPatient.secondaryDiagnosisPlaceholder")}
            />
          </Field>

          <Field>
            <Label>{t("addPatient.notes")}</Label>
            <Textarea
              placeholder={t("addPatient.notesPlaceholder")}
              value={form.notes_observations}
              onChange={set("notes_observations")}
            />
          </Field>

          <Divider />
          <SectionTitle>{t("addPatient.accountSection")}</SectionTitle>
          <Grid>
            <Field>
              <Label>{t("addPatient.username")}</Label>
              <Input
                $error={!!fe.username}
                placeholder={t("addPatient.usernamePlaceholder")}
                value={form.username}
                onChange={set("username")}
              />
              {fe.username ? <ErrorMsg>{fe.username}</ErrorMsg> : <Hint>{t("addPatient.usernameHint")}</Hint>}
            </Field>
            <Field>
              <Label>{t("addPatient.password")}</Label>
              <Input
                $error={!!fe.password}
                type="password"
                placeholder={isEdit ? "••••••••••••" : t("addPatient.passwordPlaceholder")}
                value={form.password}
                onChange={set("password")}
              />
              {fe.password
                ? <ErrorMsg>{fe.password}</ErrorMsg>
                : <Hint>{isEdit ? t("addPatient.passwordEditHint") : t("addPatient.passwordHint")}</Hint>
              }
            </Field>
          </Grid>

          <Field>
            <Label>{t("addPatient.avatar")}</Label>
            <AvatarGrid>
              {avatarList.map(({ name, src }) => (
                <AvatarOption
                  key={name}
                  src={src}
                  alt={name}
                  $selected={form.avatar === name}
                  onClick={() => setForm((prev) => ({ ...prev, avatar: name }))}
                />
              ))}
            </AvatarGrid>
          </Field>

          {globalError && <ErrorMsg>{globalError}</ErrorMsg>}

          <ActionRow>
            <CancelButton type="button" onClick={handleCancel}>
              {t("addPatient.cancel")}
            </CancelButton>
            <SubmitButton type="submit" disabled={loading}>
              {isEdit
                ? (loading ? t("addPatient.saving") : t("addPatient.saveChanges"))
                : (loading ? t("addPatient.submitting") : t("addPatient.submit"))
              }
            </SubmitButton>
          </ActionRow>
        </form>
      </Modal>

      {showDiscard && (
        <ConfirmOverlay {...discardBackdrop}>
          <ConfirmModal onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>{t("addPatient.discard.title")}</ConfirmTitle>
            <ConfirmText>{t("addPatient.discard.body")}</ConfirmText>
            <ConfirmActions>
              <ConfirmCancelButton type="button" onClick={() => setShowDiscard(false)}>
                {t("addPatient.discard.cancel")}
              </ConfirmCancelButton>
              <DiscardButton type="button" onClick={onClose}>
                {t("addPatient.discard.confirm")}
              </DiscardButton>
            </ConfirmActions>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
    </Overlay>
  );
};

export default AddPatientModal;
